// =============================================================================
// THEME HOOK
// =============================================================================

import { useState, useEffect, useCallback } from 'react';
import type { Theme } from '@/types';

const THEME_STORAGE_KEY = 'alexfinger-theme';

// Available themes
export const themes: Theme[] = [
  'light',
  'dark', 
  'high-contrast',
  'blue',
  'green',
  'purple',
  'orange'
];

// Get system preference
const getSystemTheme = (): Theme => {
  if (typeof window !== 'undefined') {
    return window.matchMedia('(prefers-color-scheme: dark)').matches 
      ? 'dark' 
      : 'orange';
  }
  return 'orange';
};

// Get stored theme or default to light
const getInitialTheme = (): Theme => {
  if (typeof window !== 'undefined') {
    const stored = localStorage.getItem(THEME_STORAGE_KEY);
    if (stored && themes.includes(stored as Theme)) {
      return stored as Theme;
    }
  }
  return 'orange';
};

// Apply theme to document
const applyTheme = (theme: Theme): void => {
  if (typeof document !== 'undefined') {
    document.documentElement.setAttribute('data-theme', theme);
  }
};

// Theme hook
export const useTheme = () => {
  const [theme, setThemeState] = useState<Theme>(getInitialTheme);
  const [isSystemTheme, setIsSystemTheme] = useState(false);

  // Apply theme when it changes
  useEffect(() => {
    applyTheme(theme);
    
    if (typeof window !== 'undefined') {
      localStorage.setItem(THEME_STORAGE_KEY, theme);
    }
  }, [theme]);

  // Listen for system theme changes
  useEffect(() => {
    if (typeof window !== 'undefined' && isSystemTheme) {
      const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
      
      const handleChange = () => {
        if (isSystemTheme) {
          setThemeState(getSystemTheme());
        }
      };

      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    }
  }, [isSystemTheme]);

  // Set specific theme
  const setTheme = useCallback((newTheme: Theme) => {
    setThemeState(newTheme);
    setIsSystemTheme(false);
  }, []);

  // Use system theme
  const setSystemTheme = useCallback(() => {
    const systemTheme = getSystemTheme();
    setThemeState(systemTheme);
    setIsSystemTheme(true);
  }, []);

  // Toggle between light and dark
  const toggleTheme = useCallback(() => {
    const newTheme = theme === 'light' ? 'dark' : 'light';
    setTheme(newTheme);
    setIsSystemTheme(false);
  }, [theme, setTheme]);

  // Get theme display name
  const getThemeDisplayName = useCallback((themeName: Theme): string => {
    const names: Record<Theme, string> = {
      light: 'Light',
      dark: 'Dark',
      'high-contrast': 'High Contrast',
      blue: 'Blue',
      green: 'Green',
      purple: 'Purple',
      orange: 'Orange',
    };
    return names[themeName];
  }, []);

  // Check if theme is active
  const isActiveTheme = useCallback((themeName: Theme): boolean => {
    return theme === themeName && !isSystemTheme;
  }, [theme, isSystemTheme]);

  return {
    theme,
    setTheme,
    setSystemTheme,
    toggleTheme,
    isSystemTheme,
    themes,
    getThemeDisplayName,
    isActiveTheme,
  };
};
